import React, { useState, useEffect } from 'react';

const ExamQueuePill = ({ status, onStart, onDismiss }) => {
    const [progress, setProgress] = useState(0);

    // Asymptotic crawl toward 95% — never freezes, no matter how long generation actually
    // takes (a few seconds for a small exam, several minutes for a large/high-complexity
    // one). A fixed-duration fill to a fixed percentage looked stalled on anything slower
    // than ~14s, which undercut the "still working on it" message right next to it. This
    // keeps nudging forward forever (each tick closes 12% of the remaining gap to 95%), so
    // it's always visibly moving, just increasingly slowly — an honest "still going" signal
    // instead of a fake ETA.
    useEffect(() => {
        if (status === 'ready') return;
        const interval = setInterval(() => {
            setProgress((p) => p + (95 - p) * 0.12);
        }, 1000);
        return () => clearInterval(interval);
    }, [status]);

    // Snap to 100% when ready
    useEffect(() => {
        if (status === 'ready') setProgress(100);
    }, [status]);

    const isReady = status === 'ready';

    return (
        <div
            className={`fixed z-40 bottom-20 sm:bottom-6 left-3 right-3 sm:left-auto sm:right-6 sm:w-80 rounded-2xl shadow-2xl border transition-all duration-300 ${
                isReady
                    ? 'border-emerald-500/40 bg-slate-900'
                    : 'border-white/10 bg-slate-900'
            }`}
            style={{ boxShadow: isReady ? '0 0 0 1px rgba(52,211,153,0.2), 0 24px 48px rgba(0,0,0,0.5)' : '0 24px 48px rgba(0,0,0,0.5)' }}
        >
            <div className="px-4 pt-3.5 pb-3">
                {/* Header row */}
                <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className={`shrink-0 h-8 w-8 rounded-xl flex items-center justify-center transition-colors ${isReady ? 'bg-emerald-500/20' : 'bg-teal-500/15'}`}>
                        {isReady ? (
                            <svg className="h-4 w-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                            </svg>
                        ) : (
                            <svg className="h-4 w-4 text-teal-400 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                        )}
                    </div>

                    {/* Text */}
                    <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold leading-tight ${isReady ? 'text-emerald-300' : 'text-white'}`}>
                            {isReady ? 'Your exam is ready!' : 'Creating your exam…'}
                        </p>
                        <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">
                            {isReady
                                ? 'Tap below to start when you\'re ready.'
                                : 'Creating high-quality questions can take a few minutes. Feel free to keep studying — we\'ll let you know when they\'re ready.'}
                        </p>
                    </div>

                    {/* Dismiss */}
                    <button
                        onClick={onDismiss}
                        className="shrink-0 -mt-0.5 rounded-full p-1 text-slate-500 hover:text-white hover:bg-white/10 transition"
                        aria-label="Dismiss"
                    >
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Progress bar */}
                <div className="mt-3 h-1.5 rounded-full bg-white/8 overflow-hidden">
                    <div
                        className={`h-full rounded-full ${isReady ? 'bg-emerald-400' : 'bg-gradient-to-r from-teal-500 to-emerald-500'}`}
                        style={{
                            width: `${progress}%`,
                            transition: progress >= 100
                                ? 'width 0.4s ease, background-color 0.3s ease'
                                : 'width 1s linear',
                        }}
                    />
                </div>

                {/* CTA — only when ready */}
                {isReady && (
                    <button
                        onClick={onStart}
                        className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 shadow-lg shadow-emerald-500/20 transition"
                    >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        Start Exam Now
                    </button>
                )}
            </div>
        </div>
    );
};

export default ExamQueuePill;
